import type { PersistedDefaultTabProps, TabProps } from '@talos/kyoko';
import { useMemo } from 'react';
import type { NavigationItem } from '../../../../tokens/navigationPaths';
import type { LayoutSpec, PanelConfig } from '../../../../types/LayoutConfig';
import { getBlotterTabRoute, getTabsStateFromPanelConfig } from './navRoutes';

export interface PanelTabNavItemsResult {
  items: NavigationItem[];
  /** id of the tab that should be considered selected (persisted selectedIndex when present, otherwise the first default tab). */
  selectedTabId: string | undefined;
}

/**
 * Resolves the tabs that should appear under a blotter panel in the mobile nav.
 *
 * Priority order:
 *  1. Persisted tabs from `panelConfig.tabs.items`, when the user has interacted with the panel.
 *  2. The blotter-specific default tabs (passed in by the caller).
 */
export function usePanelTabNavItems<T extends TabProps>({
  layout,
  panelId,
  panelConfig,
  defaultTabs,
}: {
  layout: LayoutSpec | undefined;
  panelId: string;
  panelConfig: PanelConfig | undefined;
  defaultTabs: PersistedDefaultTabProps<T>[];
}): PanelTabNavItemsResult {
  return useMemo(() => {
    if (layout == null) {
      return { items: [], selectedTabId: undefined };
    }

    const tabsState = getTabsStateFromPanelConfig(panelConfig);
    const persistedItems = tabsState?.items ?? [];
    const usingPersisted = persistedItems.length > 0;

    const sourceTabs: TabProps[] = usingPersisted ? persistedItems : defaultTabs;
    if (sourceTabs.length === 0) {
      return { items: [], selectedTabId: undefined };
    }

    const items: NavigationItem[] = sourceTabs.map((tab, index) => {
      const tabId = tab.id ?? `tab-${index}`;
      return {
        id: tabId,
        label: tab.label ?? `Tab ${index + 1}`,
        path: getBlotterTabRoute(layout, panelId, tabId),
      };
    });

    const selectedIndex = usingPersisted ? (tabsState?.selectedIndex ?? 0) : 0;
    const safeSelectedIndex = Math.min(Math.max(selectedIndex, 0), sourceTabs.length - 1);
    const selectedTabId = sourceTabs[safeSelectedIndex]?.id ?? `tab-${safeSelectedIndex}`;

    return { items, selectedTabId };
  }, [layout, panelId, panelConfig, defaultTabs]);
}
